import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Container,
  MenuItem,
  Drawer,
  Box
} from '@mui/material';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { scroller } from 'react-scroll';
import { useNavigate, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { useThemeMode } from '../context/ThemeContext';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: 12,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: '8px 12px',
}));

const scrollOptions = {
  duration: 500,
  smooth: true,
  offset: -80,
};

export default function AppAppBar() {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { mode } = useThemeMode();

  // The bar is fixed and translucent. At the top of the home page it sits over the
  // blue hero, everywhere else over the near-white page, so the reversed lockup is
  // only correct in the former case.
  const scrolledPastHero = useScrollTrigger({ disableHysteresis: true, threshold: 60 });
  const logoOnDarkGround = mode === 'dark' || (isHomePage && !scrolledPastHero);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const handleNavClick = (to) => {
    if (isHomePage) {
      scroller.scrollTo(to, scrollOptions);
    } else {
      navigate('/');
      setTimeout(() => scroller.scrollTo(to, scrollOptions), 100);
    }
  };

  const navItems = [
    { label: 'Home', to: 'home' },
    { label: 'About', to: 'about' },
    { label: 'Services', to: 'ai-use-cases' },
    { label: 'Expertise', to: 'expertise' },
    { label: 'Success', to: 'success-metrics' },
    { label: 'Contact', to: 'contact' },
    { label: 'Blog', to: '/blogs', isRoute: true },
  ];

  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 'calc(var(--template-frame-height, 0px))',
      }}
    >
      <Container maxWidth="xl">
        <StyledToolbar variant="dense" disableGutters>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            width: '100%',
            justifyContent: 'space-between',
            flexWrap: 'nowrap'
          }}>
            {/* Logo + Brand */}
            <Box 
              sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, cursor: 'pointer' }} 
              onClick={() => navigate('/')}
            >
              <Box
                component="img"
                src={logoOnDarkGround ? '/netrivium-lockup-dark.png' : '/netrivium-lockup-light.png'}
                alt="Netrivium Technologies"
                sx={{
                  height: 48,
                  width: 'auto',
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  }
                }}
              />
            </Box>

            {/* Nav Links */}
            <Box sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              gap: 1.5, 
              alignItems: 'center',
              flexShrink: 0
            }}>
              {navItems.map((item, index) => (
                <Button
                  key={index}
                  variant="text"
                  sx={{
                    color: 'text.primary',
                    px: 1.5,
                    whiteSpace: 'nowrap',
                    minWidth: 'auto',
                  }}
                  size="small"
                  onClick={() => {
                    if (item.isRoute) {
                      navigate(item.to);
                    } else {
                      handleNavClick(item.to);
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>

            {/* Theme Toggle & CTA */}
            <Box sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              gap: 1, 
              alignItems: 'center',
              flexShrink: 0
            }}>
              <ThemeToggle />
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => handleNavClick('book-demo')}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Book a Demo
              </Button>
            </Box>
          </Box>

          {/* Mobile Menu */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
            <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="top"
              open={open}
              onClose={toggleDrawer(false)}
              PaperProps={{ sx: { top: 'var(--template-frame-height, 0px)' } }}
            >
              <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>

                {navItems.map((item, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => {
                      if (item.isRoute) {
                        navigate(item.to);
                      } else {
                        handleNavClick(item.to);
                      }
                      setOpen(false);
                    }}
                  >
                    {item.label}
                  </MenuItem>
                ))}

                <MenuItem
                  onClick={() => {
                    handleNavClick('book-demo');
                    setOpen(false);
                  }}
                >
                  Book a Demo
                </MenuItem>
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
}

